import React, { useMemo } from 'react';
import Select, { components as selectComponents, Props as SelectProps, StylesConfig } from 'react-select-5';
import darkSelectStyles from './darkSelectStyles';
import { useSPAThemeScope } from './SPAThemeScope';

// AIDEV-NOTE: SPA-themed react-select wrapper. Always applies darkSelectStyles
// so consumers don't need to import and pass styles manually.
// Consumer `styles` prop composes per-key (not shallow-replaces), so overriding
// e.g. `control` still preserves the dark theme's base styling.
const mergeStyles = <Option, IsMulti extends boolean>(
  overrides?: StylesConfig<Option, IsMulti>,
): StylesConfig<Option, IsMulti> | undefined => {
  if (!overrides) return darkSelectStyles as unknown as StylesConfig<Option, IsMulti>;

  const merged = { ...darkSelectStyles } as Record<string, unknown>;

  (Object.keys(overrides) as Array<keyof StylesConfig<Option, IsMulti>>).forEach((key) => {
    const baseFn = darkSelectStyles[key as keyof typeof darkSelectStyles];
    const overrideFn = overrides[key];

    if (baseFn && overrideFn) {
      merged[key as string] = (base: Record<string, unknown>, state: unknown) => (
        (overrideFn as Function)((baseFn as Function)(base, state), state)
      );
    } else {
      merged[key as string] = overrideFn ?? baseFn;
    }
  });

  return merged as StylesConfig<Option, IsMulti>;
};

// react-select-5 (5.7) doesn't re-export MenuPortalProps from its root, so take the type from the
// `components` prop instead of reaching into dist/.
type Components<O, M extends boolean> = NonNullable<SelectProps<O, M>['components']>;
type MenuPortalProps<O, M extends boolean> = React.ComponentProps<NonNullable<Components<O, M>['MenuPortal']>>;

const SPASelect = <Option, IsMulti extends boolean = false>(
  { components, styles, ...rest }: SelectProps<Option, IsMulti>,
  // eslint-disable-next-line @typescript-eslint/indent
) => {
  const themeScope = useSPAThemeScope();

  // A portalled menu (menuPortalTarget) lands in <body>, outside a legacy mount's pinned theme
  // div, so carry the scope class with it the way SPADialog does. Memoised so react-select sees a
  // stable component and doesn't remount the open menu every render.
  const MenuPortal = useMemo(() => {
    if (!themeScope) return undefined;

    const ThemedMenuPortal = ({ children, ...props }: MenuPortalProps<Option, IsMulti>) => (
      <selectComponents.MenuPortal {...props}>
        <div className={themeScope}>{children}</div>
      </selectComponents.MenuPortal>
    );

    return ThemedMenuPortal;
  }, [themeScope]);

  return (
    <Select<Option, IsMulti>
      {...rest}
      components={MenuPortal ? { ...components, MenuPortal } : components}
      styles={mergeStyles<Option, IsMulti>(styles)}
    />
  );
};

export default SPASelect;
